import type { Booking, BookingPayment } from '../api/bookings';
import { formatDate, formatDateTime } from '../format/dates';
import { formatAmount } from '../format/money';
import type { DepositCollectionPoint } from '../generated/enums';
import { depositSplit } from './deposit';
import { isNumbered, referenceLabel } from './reference';

/**
 * What the counter says the moment a booking is taken — the booking redesign's "Booked" screen,
 * worded once for the website and the phone.
 *
 * <p>
 * <strong>It answers the three things the customer is waiting to hear</strong>: is it done (and
 * what number to quote), what was paid just now (with the receipt to send them), and what is owed
 * when they come back. Everything else is the booking's own screen, one tap away.
 * </p>
 *
 * <p>
 * <strong>The heading follows how it started, not a single "Booked!"</strong>: a hold lapses, and a
 * quotation or an enquiry set nothing aside — telling a customer "booked" for a quotation is how a
 * shop loses a wedding to the next counter.
 * </p>
 */
export interface Booked {
  /** "Booked", "On hold", "Quotation saved", "Enquiry saved". */
  title: string;
  /** The number to quote — "BK-2026-27-0031", or "Enquiry — not numbered". */
  reference: string;
  /** Over the number: "Quote this number", or why there is none yet. */
  referenceLabel: string;
  /** What that means for the garments, in one sentence. */
  sentence: string;
  facts: BookedLine[];
  money: BookedLine[];
  /** The token taken just now, for its receipt; null when none was. */
  token: BookingPayment | null;
  /**
   * The deposit taken in the same press, printed on the token's receipt (`receipt.ts`'s
   * `together`); empty when none was. When there was no token, the deposit alone is {@link token}'s
   * place on the receipt — see {@link receiptPayments}.
   */
  together: BookingPayment[];
  /** Said out loud when the booking saved but the money did not record. */
  tokenProblem: string | null;
}

export interface BookedLine {
  label: string;
  value: string;
  /** The figure the customer pays next — drawn strong. */
  strong?: boolean;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const depositWhen: Record<DepositCollectionPoint, string> = {
  AtBooking: 'due now',
  AtPickup: 'due at pickup',
  Split: 'half now, half at pickup',
};

export function booked(
  booking: Booking,
  { tokenFailed = false, currency = '' }: { tokenFailed?: boolean; currency?: string } = {},
): Booked {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const dates = `${formatDate(booking.possessionFromDate)} to ${formatDate(booking.possessionToDate)}`;
  const latest = (kind: BookingPayment['kind']) =>
    booking.payments.filter((payment) => payment.kind === kind).sort((a, b) => b.id - a.id)[0] ??
    null;
  const token = latest('Token');
  // Taken with the token in one press when the shop takes the deposit at booking.
  const depositPaid = latest('Deposit');

  const [title, sentence] = ((): [string, string] => {
    switch (booking.stage) {
      case 'Hold':
        return [
          'On hold',
          booking.holdExpiresOn
            ? `Set aside until ${formatDateTime(booking.holdExpiresOn)} — confirm before then, or the garments go back on the rail.`
            : 'Set aside for now — confirm it, or the garments go back on the rail.',
        ];
      case 'Quotation':
        return [
          'Quotation saved',
          'Nothing is set aside — a quotation reserves no stock until it is confirmed.',
        ];
      case 'Enquiry':
        return ['Enquiry saved', 'Nothing is set aside — confirm it when the customer decides.'];
      default:
        return ['Booked', `The garments are set aside from ${dates}.`];
    }
  })();

  // **The lines add up, top to bottom** (reported 2026-10-15): the rental before the discount, the
  // discount and the token each taken off it, then what is still owed. Until then the rental was
  // shown already net and the discount taken off it again under it, so "₹3,500 − ₹500 − ₹875" read
  // as ₹2,125 beside a "still to pay" of ₹2,625.
  const money: BookedLine[] = [{ label: 'Rental', value: cash(booking.rentalAmount ?? 0) }];

  if (booking.discount.totalAmount > 0) {
    money.push({ label: 'Discount given', value: `− ${cash(booking.discount.totalAmount)}` });
  }

  if (token) {
    money.push({ label: `Token paid · ${token.receiptNumber}`, value: `− ${cash(token.amount)}` });
  }

  money.push({ label: 'Still to pay', value: cash(Math.max(0, booking.balanceDue)), strong: true });

  // **The deposit after the rent, never among it**: it is held and comes back, so a deposit paid
  // in the same press sat between the token and "still to pay" reading as rent paid.
  if (depositPaid) {
    money.push({
      label: `Deposit paid · ${depositPaid.receiptNumber} · held, comes back`,
      value: cash(depositPaid.amount),
    });
  }

  const depositDue = depositSplit(booking.deposit).due;

  // Paid in full just now, the line above already says so; part-paid (a split), the rest is due.
  if (depositPaid && depositDue > 0) {
    money.push({ label: 'Rest of the deposit · due at pickup', value: cash(depositDue) });
  } else if (booking.deposit.amount && !depositPaid) {
    // When it is due only means something once garments are promised; a quotation owes nothing yet.
    const promised = booking.stage === 'Confirmed' || booking.stage === 'Hold';

    money.push({
      label: promised
        ? `Security deposit · ${depositWhen[booking.deposit.collectionPoint]}`
        : 'Security deposit, once confirmed',
      value: cash(booking.deposit.amount),
    });
  }

  return {
    title,
    reference: referenceLabel(booking),
    referenceLabel: isNumbered(booking)
      ? 'Quote this number'
      : 'It takes a number when it is confirmed',
    sentence,
    facts: [
      { label: 'Customer', value: booking.customerName },
      ...(booking.eventDate
        ? [
            {
              label: booking.eventKind ? booking.eventKind : 'Event',
              value: formatDate(booking.eventDate),
            },
          ]
        : []),
      { label: 'Out of the shop', value: dates },
      {
        label: 'Party',
        value: `${plural(booking.wearerCount, 'person', 'people')} · ${plural(booking.garmentCount, 'garment', 'garments')}`,
      },
    ],
    money,
    token,
    together: token && depositPaid ? [depositPaid] : [],
    tokenProblem: tokenFailed
      ? 'The booking is saved, but the money did not record. Take it again from the booking.'
      : null,
  };
}

/**
 * The payments the Booked screen's one receipt prints: the token with the deposit beside it, or
 * the deposit alone when no token was taken — null when nothing was.
 */
export function receiptPayments(
  words: Pick<Booked, 'token' | 'together'>,
  booking: Pick<Booking, 'payments'>,
): { payment: BookingPayment; together: BookingPayment[] } | null {
  if (words.token) {
    return { payment: words.token, together: words.together };
  }

  const deposit =
    booking.payments
      .filter((payment) => payment.kind === 'Deposit')
      .sort((a, b) => b.id - a.id)[0] ?? null;

  return deposit ? { payment: deposit, together: [] } : null;
}
